import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WaterIcon from '@mui/icons-material/Water';
import TuneIcon from '@mui/icons-material/Tune';
import BlurCircularIcon from '@mui/icons-material/BlurCircular';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { ProjectModule } from 'providers/api';
import { i18n } from 'i18n';
import useMenuState from 'components/Layout/MenuStateProvider';
import { DRAWER_WIDTH, DRAWER_WIDTH_CLOSED } from 'components/Layout/utils';
import { CONTENT_WITH_SUB_NAV_OFFSET, CONTENT_WITH_SUB_NAV_MOBILE_OFFSET } from 'components/Layout/Layout';
import { useProject } from 'modules/project/providers/project';
import { useScenario } from 'modules/project/providers/scenario';
import { useAuthorisation, Permission } from 'providers/authorisation';
import { Model } from '../../../hooks/useModelState';
import { ISceneManagerContext } from '../../../hooks/SceneManagerProvider/sceneManagerContext';
import { navigateToScene } from '../../scene-utils';
import ToolbarTooltip from './ToolbarTooltip';
import GeneralTab from './GeneralTab';
import EditTab from './EditTab';
import ImportExportTab from './ImportExportTab';
import UtilitiesTab from './UtilitiesTab';

interface ToolbarProps {
  model: Model;
  zUpMode: boolean;
  onZUpModeChange: (v: boolean) => void;
  context: ISceneManagerContext;
}

type ModuleKey = 'DEFAULT' | 'BULK_EDIT' | 'WEAR_MONITORING';
type TabKey = 'general' | 'edit' | 'importExport' | 'utilities';

const TOOLBAR_STORAGE_KEY = 'reticulationToolbarState';
// Minimum height for the tool-section row so the toolbar thickness does not change between tabs.
// Sized to the tallest section content (split buttons with their caret row). The row grows only
// when a horizontal scrollbar appears, so the scrollbar never steals height from the content.
const CONTENT_ROW_HEIGHT = 66;

const readStoredCollapsed = (): boolean => {
  try {
    const raw = window.localStorage.getItem(TOOLBAR_STORAGE_KEY);
    if (!raw) return false;
    return !!JSON.parse(raw)?.collapsed;
  } catch {
    return false;
  }
};

const resolveModuleValue = (sceneKey: string | undefined): ModuleKey | null => {
  if (sceneKey === 'BULK_EDIT' || sceneKey === 'BULK_IMPORT_SOLIDS') return 'BULK_EDIT';
  if (sceneKey === 'WEAR_MONITORING') return 'WEAR_MONITORING';
  if (sceneKey === 'DEFAULT' || sceneKey === 'STOPE_SELECTED') return 'DEFAULT';
  return null;
};

const Toolbar = ({ model, zUpMode, onZUpModeChange, context }: ToolbarProps) => {
  const [activeTab, setActiveTab] = React.useState<TabKey>('general');
  const [isCollapsed, setIsCollapsed] = React.useState(() => readStoredCollapsed());
  const { state: { projectId, clientId, enabledModules } } = useProject();
  const { functions: { validatePermission } } = useAuthorisation();
  const { activeScenarioName } = useScenario();
  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });
  const menuOpen = useMenuState();
  const paperRef = React.useRef<HTMLDivElement | null>(null);
  const topOffset = isLarge ? CONTENT_WITH_SUB_NAV_OFFSET : CONTENT_WITH_SUB_NAV_MOBILE_OFFSET;
  const leftOffset = menuOpen ? DRAWER_WIDTH : DRAWER_WIDTH_CLOSED;
  const wearMonitoringEnabled = enabledModules.includes(ProjectModule.WearMonitoring);
  const canBulkEdit = validatePermission(Permission.ManageNodeData, [projectId, clientId]);
  const currentSceneKey = context.state.currentScene?.key;
  const moduleValue = resolveModuleValue(currentSceneKey);
  const bulkEditTabsEnabled = currentSceneKey === 'BULK_EDIT';
  const isScenarioManagerOpen = currentSceneKey === 'SCENARIO_MANAGER';

  const isBulkEditOnlyTab = activeTab === 'edit' || activeTab === 'importExport';
  const effectiveActiveTab: TabKey = isBulkEditOnlyTab && !bulkEditTabsEnabled ? 'general' : activeTab;

  const handleModuleChange = (_: React.MouseEvent<HTMLElement>, value: ModuleKey | null) => {
    if (!value || value === moduleValue) return;
    if (value === 'BULK_EDIT') {
      setActiveTab('edit');
      navigateToScene('BULK_EDIT', { model, context });
    } else if (value === 'WEAR_MONITORING') {
      setActiveTab('general');
      context.actions.setDrawerOpen(true);
      navigateToScene('WEAR_MONITORING', { model, context });
    } else {
      setActiveTab('general');
      navigateToScene('DEFAULT', { model, context });
    }
  };

  const handleScenarioManagerClick = () => {
    if (isScenarioManagerOpen) {
      navigateToScene('DEFAULT', { model, context });
      return;
    }
    setActiveTab('general');
    navigateToScene('SCENARIO_MANAGER', { model, context });
  };

  const handleTabChange = (_: React.SyntheticEvent, value: TabKey) => {
    setActiveTab(value);
    setIsCollapsed(false);
  };

  React.useEffect(() => {
    if ((activeTab === 'edit' || activeTab === 'importExport') && !bulkEditTabsEnabled) {
      setActiveTab('general');
    }
  }, [bulkEditTabsEnabled, activeTab]);

  React.useEffect(() => {
    try {
      window.localStorage.setItem(TOOLBAR_STORAGE_KEY, JSON.stringify({ collapsed: isCollapsed }));
    } catch {
      // localStorage unavailable — collapse state simply won't persist
    }
  }, [isCollapsed]);

  // Publish the toolbar height so the stage content and side drawers can start below it.
  React.useLayoutEffect(() => {
    const el = paperRef.current;
    if (!el) return undefined;
    const publishHeight = () => {
      document.documentElement.style.setProperty('--stopes-toolbar-height', `${el.getBoundingClientRect().height}px`);
    };
    publishHeight();
    const observer = new ResizeObserver(publishHeight);
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--stopes-toolbar-height');
    };
  }, []);

  return (
    <Paper
      ref={paperRef}
      square
      elevation={1}
      sx={{
        position: 'fixed',
        top: `${topOffset}px`,
        left: `${leftOffset}px`,
        right: 0,
        // Above the scene side drawers (1000), below the app TopBar (zIndex.drawer + 1).
        zIndex: 1150,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, minHeight: 35, overflowX: 'auto', overflowY: 'hidden' }}>
        <ToggleButtonGroup
          value={moduleValue}
          exclusive
          size="small"
          onChange={handleModuleChange}
          sx={{
            '& .MuiToggleButton-root': {
              py: 0.25,
              px: 1,
              fontSize: 11,
              textTransform: 'none',
              lineHeight: 1.2,
              gap: 0.5,
              border: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          <ToolbarTooltip
            title={i18n._('Hydraulic')}
            description={i18n._('View the reticulation network and explore hydraulic flow paths.')}
          >
            <ToggleButton value="DEFAULT" aria-label={i18n._('Hydraulic')}>
              <WaterIcon fontSize="small" />
              {i18n._('Hydraulic')}
            </ToggleButton>
          </ToolbarTooltip>
          {canBulkEdit && (
            <ToolbarTooltip
              title={i18n._('Editor')}
              description={i18n._('Select multiple nodes, spools or stopes to edit, group, or delete in one operation.')}
            >
              <ToggleButton value="BULK_EDIT" aria-label={i18n._('Editor')}>
                <TuneIcon fontSize="small" />
                {i18n._('Editor')}
              </ToggleButton>
            </ToolbarTooltip>
          )}
          {wearMonitoringEnabled && (
            <ToolbarTooltip
              title={i18n._('Wear Monitoring')}
              description={i18n._('Inspect pipe wear measurements and manage inspection points.')}
            >
              <ToggleButton value="WEAR_MONITORING" aria-label={i18n._('Wear Monitoring')}>
                <BlurCircularIcon fontSize="small" />
                {i18n._('Wear')}
              </ToggleButton>
            </ToolbarTooltip>
          )}
        </ToggleButtonGroup>
        <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
        <Tabs
          value={effectiveActiveTab}
          onChange={handleTabChange}
          textColor="inherit"
          indicatorColor="secondary"
          sx={{
            minHeight: 34,
            alignSelf: 'stretch',
            '& .MuiTab-root': {
              minHeight: 34,
              py: 0.25,
              px: 1.5,
              fontSize: 11,
              textTransform: 'none',
              color: 'inherit',
              '&.Mui-selected': { color: 'inherit' },
            },
          }}
        >
          <Tab value="general" label={i18n._('General')} />
          {canBulkEdit && <Tab value="edit" label={i18n._('Edit')} disabled={!bulkEditTabsEnabled} />}
          {canBulkEdit && <Tab value="importExport" label={i18n._('Import/Export')} disabled={!bulkEditTabsEnabled} />}
          <Tab value="utilities" label={i18n._('Utilities')} />
        </Tabs>
        <Box sx={{ flexGrow: 1 }} />
        {canBulkEdit && (
          <ToolbarTooltip
            title={i18n._('Scenario Manager')}
            description={i18n._('Branch the model into what-if scenarios to explore changes without affecting master. Shows the scenario you are currently working in.')}
          >
            <ToggleButton
              value="SCENARIO_MANAGER"
              size="small"
              selected={isScenarioManagerOpen}
              onClick={handleScenarioManagerClick}
              aria-label={i18n._('Scenario Manager')}
              sx={{
                py: 0.25,
                px: 1,
                fontSize: 11,
                textTransform: 'none',
                lineHeight: 1.2,
                gap: 0.5,
                border: '1px solid',
                borderColor: 'divider',
                whiteSpace: 'nowrap',
              }}
            >
              <AccountTreeIcon fontSize="small" />
              {i18n._('Scenario: {name}', { name: activeScenarioName ?? i18n._('Master') })}
            </ToggleButton>
          </ToolbarTooltip>
        )}
        <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
        <ToolbarTooltip
          title={isCollapsed ? i18n._('Show Toolbar') : i18n._('Collapse Toolbar')}
          description={isCollapsed
            ? i18n._('Show the tool sections again. Clicking any tab also expands the toolbar.')
            : i18n._('Hide the tool sections and keep only this bar, like collapsing the ribbon in Word or Excel.')}
        >
          <IconButton
            size="small"
            aria-label={isCollapsed ? i18n._('Show toolbar') : i18n._('Collapse toolbar')}
            onClick={() => setIsCollapsed((prev) => !prev)}
          >
            {isCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
          </IconButton>
        </ToolbarTooltip>
      </Box>
      {!isCollapsed && <Divider />}
      {!isCollapsed && (
        <Box sx={{ display: 'flex', alignItems: 'stretch', py: 0.5, px: 0.5, overflowX: 'auto', overflowY: 'hidden', minHeight: CONTENT_ROW_HEIGHT }}>
          {effectiveActiveTab === 'general' && (
            <GeneralTab
              model={model}
              zUpMode={zUpMode}
              onZUpModeChange={onZUpModeChange}
              context={context}
            />
          )}
          {bulkEditTabsEnabled && effectiveActiveTab === 'edit' && <EditTab model={model} />}
          {bulkEditTabsEnabled && effectiveActiveTab === 'importExport' && <ImportExportTab model={model} context={context} />}
          {effectiveActiveTab === 'utilities' && <UtilitiesTab model={model} />}
        </Box>
      )}
    </Paper>
  );
};

export default Toolbar;
